import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { ArrowLeft, Save, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { EditorSidebar } from './editor/EditorSidebar';
import { SectionEditor } from './editor/SectionEditor';
import { AddSectionModal } from './editor/AddSectionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-scoped unique id generator (avoids same-millisecond collisions and
// keeps id creation out of the component render path).
let sectionIdSeq = 0;
const makeSectionId = () => `section_${Date.now()}_${sectionIdSeq++}`;

const WebsiteEditor = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [customCSS, setCustomCSS] = useState('');
  const [activeTab, setActiveTab] = useState('sections'); // sections, styles, code
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/website/projects/${projectId}`, {
          withCredentials: true
        });
        if (!active) return;
        setProject(response.data);
        setCustomCSS(response.data.custom_styles || '');
      } catch (error) {
        if (!active) return;
        toast.error('Failed to load project');
        navigate('/admin/dashboard');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId, navigate]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/website/projects/${projectId}`, {
        sections: project.sections,
        custom_styles: customCSS,
        name: project.name
      }, {
        withCredentials: true
      });
      toast.success('Project saved!');
    } catch (error) {
      toast.error('Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSection = (sectionId, updates) => {
    setProject(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    setProject(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
    setSelectedSection(null);
  };

  const moveSection = (index, direction) => {
    const newSections = [...project.sections];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setProject(prev => ({ ...prev, sections: newSections }));
  };

  const addSection = (type) => {
    const newSection = {
      id: makeSectionId(),
      type,
      title: `New ${type.charAt(0).toUpperCase() + type.slice(1)} Section`,
      subtitle: '',
      content: 'Add your content here...',
      items: [],
      styles: {
        backgroundColor: '#1a1c1b',
        textColor: '#7dd3fc',
        accentColor: '#7dd3fc'
      },
      layout: 'centered'
    };
    setProject(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setShowAddSection(false);
    setSelectedSection(newSection.id);
  };

  const duplicateSection = (section) => {
    const newSection = {
      ...section,
      id: makeSectionId(),
      title: `${section.title} (Copy)`
    };
    const index = project.sections.findIndex(s => s.id === section.id);
    const newSections = [...project.sections];
    newSections.splice(index + 1, 0, newSection);
    setProject(prev => ({ ...prev, sections: newSections }));
  };

  const regenerateSection = async (sectionId) => {
    if (!regeneratePrompt) {
      toast.error('Please describe what changes you want');
      return;
    }

    setIsRegenerating(true);
    try {
      const response = await axios.post(`${API}/website/projects/${projectId}/regenerate-section`, {
        section_id: sectionId,
        prompt: regeneratePrompt
      }, {
        withCredentials: true
      });

      setProject(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId ? response.data : s
        )
      }));

      toast.success('Section regenerated!');
      setRegeneratePrompt('');
    } catch (error) {
      toast.error('Failed to regenerate section');
    } finally {
      setIsRegenerating(false);
    }
  };

  const updateGlobalStyles = (key, value) => {
    setProject(prev => ({
      ...prev,
      generated_code: {
        ...prev.generated_code,
        globalStyles: {
          ...prev.generated_code?.globalStyles,
          [key]: value
        }
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const globalStyles = project.generated_code?.globalStyles || {};
  const currentSection = project.sections?.find(s => s.id === selectedSection);

  return (
    <div className="min-h-screen bg-[#1a1c1b] flex flex-col">
      {/* Header */}
      <header className="bg-[#302f2c] border-b border-[#1e3a5f]/50 sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="ghost"
                className="text-[#888680] hover:text-[#7dd3fc] p-2"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <Input
                  value={project.name}
                  onChange={(e) => setProject({ ...project, name: e.target.value })}
                  className="bg-transparent border-none text-[#7dd3fc] font-bold text-lg p-0 h-auto focus:ring-0"
                />
                <p className="text-[#888680] text-xs">Last saved: {new Date(project.updated_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate(`/admin/preview/${projectId}`)}
                variant="outline"
                className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full px-4 py-2 text-sm"
              >
                <Eye size={16} className="mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                data-testid="save-project-btn"
                className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-4 py-2 text-sm font-semibold"
              >
                {isSaving ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Save size={16} className="mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <EditorSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sections={project.sections}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          moveSection={moveSection}
          onAddSection={() => setShowAddSection(true)}
          globalStyles={globalStyles}
          updateGlobalStyles={updateGlobalStyles}
          customCSS={customCSS}
          setCustomCSS={setCustomCSS}
        />

        <SectionEditor
          section={currentSection}
          updateSection={updateSection}
          duplicateSection={duplicateSection}
          deleteSection={deleteSection}
          regeneratePrompt={regeneratePrompt}
          setRegeneratePrompt={setRegeneratePrompt}
          regenerateSection={regenerateSection}
          isRegenerating={isRegenerating}
        />
      </div>

      {showAddSection && (
        <AddSectionModal
          onAdd={addSection}
          onClose={() => setShowAddSection(false)}
        />
      )}
    </div>
  );
};

export default WebsiteEditor;
